/**
 * Knowledge Hub — video catalogue page for authenticated users.
 * AC2: Filter bar (All/Starter/Intermediate/Advanced), search input, responsive grid.
 * AC3: Progress bars on video cards.
 * AC6: Brand video pinned first, labelled "About my investments", no difficulty badge.
 * AC7: KnowledgeHubOnboarding modal on first visit (localStorage key).
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Play, Lock, Search, CheckCircle, FileText, Video, BookA } from 'lucide-react';
import KnowledgeHubOnboarding from '../components/knowledge/KnowledgeHubOnboarding';
import ThumbnailImg from '../components/knowledge/ThumbnailImg';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';
const ONBOARDING_KEY = 'knowledgeHubOnboardingSeen';

type DifficultyFilter = 'all' | 'starter' | 'intermediate' | 'advanced';
type HubTab = 'articles' | 'videos' | 'glossary';

/* ---------- Types ---------- */

interface MarketingItem {
  id: string;
  title: string;
  summary?: string;
  body?: string;
  type: string;
  published_at?: string;
  thumbnail_url?: string;
  url?: string;
}

interface MarketingFeed {
  items: MarketingItem[];
  total: number;
}

interface Video {
  id: string;
  title: string;
  description?: string;
  video_type: string;
  difficulty?: string;
  topic_tags: string[];
  duration_sec?: number;
  thumbnail_key?: string;
  is_published: boolean;
  is_public: boolean;
}

interface VideosPage {
  items: Video[];
  total: number;
}

interface BrandVideoData {
  available: boolean;
  url?: string;
  title?: string;
}

interface ProgressData {
  watched_seconds: number;
  completed: boolean;
}

type ProgressMap = Record<string, ProgressData>;

/* ---------- API helpers ---------- */

async function fetchVideos(token: string): Promise<VideosPage> {
  const res = await fetch(`${API_BASE}/api/v1/knowledge/videos`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('videos fetch failed');
  return res.json();
}

async function fetchBrandVideo(): Promise<BrandVideoData> {
  const res = await fetch(`${API_BASE}/api/v1/knowledge/brand-video`);
  if (!res.ok) return { available: false };
  return res.json();
}

async function fetchProgressBatch(token: string, videoIds: string[]): Promise<ProgressMap> {
  if (!videoIds.length) return {};
  const params = videoIds.map((id) => `video_ids=${encodeURIComponent(id)}`).join('&');
  const res = await fetch(`${API_BASE}/api/v1/knowledge/videos/progress-batch?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return {};
  return res.json();
}

async function fetchMarketingArticles(): Promise<MarketingFeed> {
  const res = await fetch(`${API_BASE}/api/v1/knowledge/marketing/articles`);
  if (!res.ok) return { items: [], total: 0 };
  return res.json();
}

async function fetchMarketingGlossary(): Promise<MarketingFeed> {
  const res = await fetch(`${API_BASE}/api/v1/knowledge/marketing/glossary`);
  if (!res.ok) return { items: [], total: 0 };
  return res.json();
}

/* ---------- Sub-components ---------- */

const ArticleCard: React.FC<{ item: MarketingItem }> = ({ item }) => (
  <a
    href={item.url || '#'}
    target="_blank"
    rel="noopener noreferrer"
    className="group relative text-left rounded-2xl overflow-hidden border border-slate-700/50 bg-slate-800/50 hover:border-teal-500/40 transition-all hover:-translate-y-0.5 block"
  >
    {item.thumbnail_url ? (
      <div className="h-40 bg-slate-900">
        <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
      </div>
    ) : (
      <div className="h-40 bg-slate-900 flex items-center justify-center">
        <FileText size={32} className="text-slate-600" />
      </div>
    )}
    <div className="p-4">
      <h3 className="text-sm font-semibold text-white leading-snug mb-1">{item.title}</h3>
      {item.summary && (
        <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{item.summary}</p>
      )}
      {item.published_at && (
        <p className="text-[10px] text-slate-600 mt-2">{new Date(item.published_at).toLocaleDateString()}</p>
      )}
    </div>
  </a>
);

const GlossaryCard: React.FC<{ item: MarketingItem }> = ({ item }) => (
  <div className="rounded-2xl border border-slate-700/50 bg-slate-800/50 p-4">
    <h3 className="text-sm font-semibold text-teal-400 mb-1">{item.title}</h3>
    {item.summary && (
      <p className="text-xs text-slate-400 leading-relaxed">{item.summary}</p>
    )}
    {item.body && !item.summary && (
      <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{item.body}</p>
    )}
  </div>
);

const DifficultyBadge: React.FC<{ difficulty?: string }> = ({ difficulty }) => {
  if (!difficulty) return null;
  const colours: Record<string, string> = {
    starter: 'bg-green-500/20 text-green-400',
    intermediate: 'bg-amber-500/20 text-amber-400',
    advanced: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ${colours[difficulty] ?? 'bg-slate-500/20 text-slate-400'}`}>
      {difficulty}
    </span>
  );
};

const formatDuration = (secs?: number) => {
  if (!secs) return '';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

interface VideoCardProps {
  video: Video;
  progress?: ProgressData;
  isBrand?: boolean;
  onClick: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, progress, isBrand, onClick }) => {
  const pct = video.duration_sec && progress && progress.watched_seconds > 0
    ? Math.min(100, Math.round((progress.watched_seconds / video.duration_sec) * 100))
    : 0;

  return (
    <button
      onClick={onClick}
      className="group relative text-left rounded-2xl overflow-hidden border border-slate-700/50 bg-slate-800/50 hover:border-teal-500/40 transition-all hover:-translate-y-0.5 w-full"
    >
      {/* Thumbnail */}
      <div className="relative h-40 bg-slate-900 flex items-center justify-center">
        {video.thumbnail_key ? (
          /* H1 fix: use signed URL via ThumbnailImg, not raw GCS storage key */
          <ThumbnailImg videoId={video.id} alt={video.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
        ) : (
          <BookOpen size={32} className="text-slate-600" />
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
            <Play size={18} className="text-slate-900 ml-0.5" />
          </div>
        </div>
        {video.duration_sec && (
          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
            {formatDuration(video.duration_sec)}
          </span>
        )}
        {/* AC6: completed badge */}
        {progress?.completed && (
          <span className="absolute top-2 left-2 flex items-center gap-1 bg-green-600/90 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
            <CheckCircle size={10} />
            Completed
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-white leading-snug flex-1">
            {isBrand ? 'About my investments' : video.title}
          </h3>
          {!isBrand && <DifficultyBadge difficulty={video.difficulty} />}
        </div>
        {video.description && (
          <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{video.description}</p>
        )}
        {!isBrand && video.topic_tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {video.topic_tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400">{tag}</span>
            ))}
          </div>
        )}
        {/* AC3: progress bar */}
        {pct > 0 && !progress?.completed && (
          <div className="mt-3">
            <div className="flex justify-between text-[10px] text-slate-500 mb-1">
              <span>{pct}% watched</span>
            </div>
            <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

/* ---------- Page ---------- */

export default function KnowledgeHub() {
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activeTab, setActiveTab] = useState<HubTab>('articles');
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyFilter>('all');
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('token') ?? '';

  const { data: brandData } = useQuery<BrandVideoData>({
    queryKey: ['brand-video'],
    queryFn: fetchBrandVideo,
    staleTime: 3_600_000,
    retry: false,
  });

  const { data: videosData, isLoading } = useQuery<VideosPage>({
    queryKey: ['knowledge-videos'],
    queryFn: () => fetchVideos(token),
    staleTime: 300_000,
    enabled: !!token,
  });

  const allVideoIds = useMemo(
    () => (videosData?.items ?? []).map((v) => v.id),
    [videosData],
  );

  const { data: progressMap = {} } = useQuery<ProgressMap>({
    queryKey: ['knowledge-progress-batch', allVideoIds],
    queryFn: () => fetchProgressBatch(token, allVideoIds),
    enabled: !!token && allVideoIds.length > 0,
    staleTime: 60_000,
  });

  // Marketing content feed queries (v33-1-1)
  const { data: marketingArticles, isLoading: articlesLoading } = useQuery<MarketingFeed>({
    queryKey: ['marketing-articles'],
    queryFn: fetchMarketingArticles,
    staleTime: 300_000,
    enabled: activeTab === 'articles',
  });

  const { data: marketingGlossary, isLoading: glossaryLoading } = useQuery<MarketingFeed>({
    queryKey: ['marketing-glossary'],
    queryFn: fetchMarketingGlossary,
    staleTime: 300_000,
    enabled: activeTab === 'glossary',
  });

  // AC7: Show onboarding modal on first visit
  useEffect(() => {
    if (brandData !== undefined && !localStorage.getItem(ONBOARDING_KEY)) {
      setShowOnboarding(true);
    }
  }, [brandData]);

  const regularVideos = (videosData?.items ?? []).filter((v) => v.video_type !== 'brand');
  const hasBrand = brandData?.available;

  // AC2: client-side filter
  const filteredVideos = useMemo(() => {
    let videos = regularVideos;
    if (difficultyFilter !== 'all') {
      videos = videos.filter((v) => v.difficulty === difficultyFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      videos = videos.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description?.toLowerCase().includes(q) ||
          v.topic_tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return videos;
  }, [regularVideos, difficultyFilter, search]);

  const FILTERS: { label: string; value: DifficultyFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Starter', value: 'starter' },
    { label: 'Intermediate', value: 'intermediate' },
    { label: 'Advanced', value: 'advanced' },
  ];

  const TABS: { label: string; value: HubTab; icon: React.ReactNode }[] = [
    { label: 'Articles', value: 'articles', icon: <FileText size={14} /> },
    { label: 'Videos', value: 'videos', icon: <Video size={14} /> },
    { label: 'Glossary', value: 'glossary', icon: <BookA size={14} /> },
  ];

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {Array(8).fill(null).map((_, i) => (
        <div key={i} className="rounded-2xl bg-slate-800/50 animate-pulse">
          <div className="h-40 bg-slate-700/40 rounded-t-2xl" />
          <div className="p-4 space-y-2">
            <div className="h-3 bg-slate-700/40 rounded w-3/4" />
            <div className="h-3 bg-slate-700/40 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderEmpty = (msg: string) => (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-slate-600">
      <BookOpen size={32} className="mb-3" />
      <p className="text-sm">{msg}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <BookOpen size={22} className="text-teal-400" />
            <h1 className="text-2xl font-bold">Knowledge Hub</h1>
          </div>
          <p className="text-slate-500 text-sm ml-[34px]">
            Articles, videos, and insights to improve your investing.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-slate-800 pb-3">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setActiveTab(t.value)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === t.value
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Articles tab (marketing feed) ── */}
        {activeTab === 'articles' && (
          <>
            {articlesLoading ? renderSkeleton() : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {(marketingArticles?.items ?? []).length > 0
                  ? (marketingArticles?.items ?? []).map((a) => <ArticleCard key={a.id} item={a} />)
                  : renderEmpty('Knowledge hub coming soon. Check back for articles and insights.')}
              </div>
            )}
          </>
        )}

        {/* ── Videos tab (existing) ── */}
        {activeTab === 'videos' && (
          <>
            {/* Difficulty filter + search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
              <div className="flex gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setDifficultyFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      difficultyFilter === f.value
                        ? 'bg-teal-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search videos..."
                  className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50"
                />
              </div>
            </div>

            {isLoading && !hasBrand ? renderSkeleton() : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {hasBrand && brandData?.url && difficultyFilter === 'all' && !search && (
                  <VideoCard
                    video={{
                      id: 'brand',
                      title: 'About my investments',
                      video_type: 'brand',
                      topic_tags: [],
                      is_published: true,
                      is_public: true,
                    }}
                    isBrand
                    onClick={() => setShowOnboarding(true)}
                  />
                )}
                {filteredVideos.map((v) => (
                  <VideoCard
                    key={v.id}
                    video={v}
                    progress={progressMap[v.id]}
                    onClick={() => navigate(`/knowledge/${v.id}`)}
                  />
                ))}
                {!hasBrand && filteredVideos.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-20 text-slate-600">
                    <Lock size={32} className="mb-3" />
                    <p className="text-sm">No videos found. Try adjusting your filters.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Glossary tab (marketing feed) ── */}
        {activeTab === 'glossary' && (
          <>
            {glossaryLoading ? renderSkeleton() : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(marketingGlossary?.items ?? []).length > 0
                  ? (marketingGlossary?.items ?? []).map((g) => <GlossaryCard key={g.id} item={g} />)
                  : renderEmpty('Knowledge hub coming soon. Check back for glossary terms.')}
              </div>
            )}
          </>
        )}
      </div>

      {/* AC7: Onboarding modal */}
      {showOnboarding && (
        <KnowledgeHubOnboarding
          videoUrl={brandData?.url}
          onDismiss={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
